import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { 
  Users, 
  Map, 
  Beer, 
  CheckCircle2, 
  Clock, 
  Shield, 
  Crown, 
  ArrowLeft,
  Check,
  X,
  Eye,
  TrendingUp,
  AlertCircle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [rejectModal, setRejectModal] = useState({ open: false, submissionId: null });
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, submissionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/submissions/pending`),
        axios.get(`${API}/admin/users?limit=100`)
      ]);
      setStats(statsRes.data);
      setPendingSubmissions(submissionsRes.data.submissions || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      console.error("Failed to fetch admin data:", error);
      if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId) => {
    try {
      await axios.put(`${API}/submissions/${submissionId}/approve`);
      toast.success("Submission approved!");
      setPendingSubmissions(prev => prev.filter(s => s.submission_id !== submissionId));
      // Refresh stats
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to approve submission");
    }
  };

  const handleReject = async () => {
    if (!rejectModal.submissionId) return;
    try {
      await axios.put(`${API}/submissions/${rejectModal.submissionId}/reject`, {
        reason: rejectReason
      });
      toast.success("Submission rejected");
      setPendingSubmissions(prev => prev.filter(s => s.submission_id !== rejectModal.submissionId));
      setRejectModal({ open: false, submissionId: null });
      setRejectReason("");
    } catch (error) {
      toast.error("Failed to reject submission");
    }
  };

  const toggleAdmin = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-admin`);
      toast.success(res.data.message);
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, is_admin: res.data.is_admin } : u
      ));
    } catch (error) {
      toast.error("Failed to update admin status");
    }
  };

  const togglePremium = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/toggle-premium`);
      toast.success(res.data.message);
      setUsers(prev => prev.map(u => 
        u.user_id === userId ? { ...u, is_premium: res.data.is_premium } : u
      ));
    } catch (error) {
      toast.error("Failed to update premium status");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-bronze border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-24">
      {/* Header */}
      <div className="glass sticky top-0 z-40 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate("/dashboard")} className="text-gray-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="font-oswald text-lg uppercase tracking-wider flex items-center gap-2">
            <Shield className="w-5 h-5 text-red-500" />
            Admin Dashboard
          </h1>
          <p className="text-xs text-gray-500">Manage users, submissions & content</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 px-4 py-3 overflow-x-auto">
        {["overview", "submissions", "users"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-full font-oswald uppercase text-sm whitespace-nowrap transition-colors ${
              activeTab === tab 
                ? "bg-bronze text-white" 
                : "bg-white/5 text-gray-400 hover:bg-white/10"
            }`}
          >
            {tab}
            {tab === "submissions" && pendingSubmissions.length > 0 && (
              <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingSubmissions.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="px-4">
        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-4">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-cyan-400 mb-2">
                  <Users className="w-4 h-4" />
                  <span className="text-xs uppercase">Total Users</span>
                </div>
                <p className="text-2xl font-oswald font-bold">{stats.total_users}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="text-green-500">+{stats.weekly.new_users}</span> this week
                </p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-amber-400 mb-2">
                  <Crown className="w-4 h-4" />
                  <span className="text-xs uppercase">Premium</span>
                </div>
                <p className="text-2xl font-oswald font-bold">{stats.premium_users}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {((stats.premium_users / stats.total_users) * 100).toFixed(1)}% conversion
                </p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-purple-400 mb-2">
                  <Map className="w-4 h-4" />
                  <span className="text-xs uppercase">Total Trips</span>
                </div>
                <p className="text-2xl font-oswald font-bold">{stats.total_trips}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="text-green-500">+{stats.weekly.new_trips}</span> this week
                </p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs uppercase">Check-ins</span>
                </div>
                <p className="text-2xl font-oswald font-bold">{stats.total_checkins}</p>
                <p className="text-xs text-gray-500 mt-1">
                  <span className="text-green-500">+{stats.weekly.new_checkins}</span> this week
                </p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-orange-400 mb-2">
                  <Beer className="w-4 h-4" />
                  <span className="text-xs uppercase">Total Dives</span>
                </div>
                <p className="text-2xl font-oswald font-bold">{stats.total_bars}</p>
              </div>

              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-red-400 mb-2">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs uppercase">Pending</span>
                </div>
                <p className="text-2xl font-oswald font-bold">{stats.pending_submissions}</p>
                <p className="text-xs text-gray-500 mt-1">awaiting review</p>
              </div>
            </div>

            {/* Quick Actions */}
            {pendingSubmissions.length > 0 && (
              <div className="glass rounded-xl p-4">
                <div className="flex items-center gap-2 text-amber-400 mb-3">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-oswald uppercase text-sm">Action Required</span>
                </div>
                <p className="text-sm text-gray-400 mb-3">
                  You have {pendingSubmissions.length} submission(s) awaiting review.
                </p>
                <Button 
                  className="w-full bg-bronze hover:bg-bronze-600 font-oswald uppercase"
                  onClick={() => setActiveTab("submissions")}
                >
                  Review Submissions
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === "submissions" && (
          <div className="space-y-3">
            {pendingSubmissions.length === 0 ? (
              <div className="glass rounded-xl p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-gray-400">All caught up!</p>
                <p className="text-gray-600 text-sm mt-1">No pending submissions to review.</p>
              </div>
            ) : (
              pendingSubmissions.map(sub => (
                <div key={sub.submission_id} className="glass rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="px-2 py-0.5 rounded text-xs uppercase bg-purple-500/20 text-purple-400">
                        {sub.type}
                      </span>
                      <h3 className="font-oswald text-lg mt-1">{sub.name}</h3>
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  
                  {sub.description && (
                    <p className="text-sm text-gray-400 mb-2">{sub.description}</p>
                  )}
                  
                  {sub.address && (
                    <p className="text-xs text-gray-500 mb-2">📍 {sub.address}</p>
                  )}

                  {sub.photos && sub.photos.length > 0 && (
                    <div className="flex gap-2 mb-3 overflow-x-auto">
                      {sub.photos.slice(0, 3).map((photo, idx) => (
                        <img 
                          key={idx}
                          src={photo} 
                          alt="" 
                          className="w-16 h-16 rounded object-cover flex-shrink-0"
                        />
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      className="flex-1 h-10 bg-green-600 hover:bg-green-700 font-oswald uppercase text-sm"
                      onClick={() => handleApprove(sub.submission_id)}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 h-10 border-red-500/50 text-red-400 hover:bg-red-500/10 font-oswald uppercase text-sm"
                      onClick={() => setRejectModal({ open: true, submissionId: sub.submission_id })}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-2">
            {users.map(u => (
              <div key={u.user_id} className="glass rounded-xl p-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-oswald">{u.name?.charAt(0) || "?"}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{u.name || "Unknown"}</p>
                    {u.is_admin && (
                      <Shield className="w-3 h-3 text-red-500 flex-shrink-0" />
                    )}
                    {u.is_premium && (
                      <Crown className="w-3 h-3 text-amber-400 flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleAdmin(u.user_id)}
                    className={`p-2 rounded ${u.is_admin ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-gray-500'} hover:bg-white/10`}
                    title={u.is_admin ? "Remove admin" : "Make admin"}
                  >
                    <Shield className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => togglePremium(u.user_id)}
                    className={`p-2 rounded ${u.is_premium ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-gray-500'} hover:bg-white/10`}
                    title={u.is_premium ? "Remove premium" : "Grant premium"}
                  >
                    <Crown className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="glass rounded-xl p-6 max-w-sm w-full">
            <h3 className="font-oswald text-lg uppercase mb-4">Reject Submission</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Reason for rejection (optional)..."
              className="w-full h-24 bg-[#121212] border border-white/10 rounded-lg p-3 text-sm mb-4"
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setRejectModal({ open: false, submissionId: null });
                  setRejectReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleReject}
              >
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
